import * as React from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { BarChart, LineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { gettext } from "i18n";
import { formatNumberLocale } from "utils/formatting";
import { getChartSeriesColor } from "utils/datacharts";

echarts.use([
  GridComponent,
  TooltipComponent,
  LegendComponent,
  LineChart,
  BarChart,
  CanvasRenderer,
]);

interface LoadCalculationPeriod {
  key: string;
  label: string;
  cumulativeFlow: number;
  loadParameters: Array<{
    averageConcentration: number;
    sampleCount: number;
    totalLoad: number;
    interpolated: boolean;
  }>;
}

interface LoadCalculationChartProps {
  periods: LoadCalculationPeriod[];
  loadParameters: Array<{ name: string; unit?: string }>;
  showResultsIn: "line_graph" | "bar_graph";
  height?: string;
}

const LoadCalculationChart: React.FC<LoadCalculationChartProps> = ({
  periods,
  loadParameters,
  showResultsIn,
  height = "300px",
}) => {
  const chartOptions = React.useMemo(() => {
    if (!periods?.length || !loadParameters?.length) {
      return null;
    }

    const xAxisData = periods.map((p) => p.label);
    const series = loadParameters.map((param, paramIndex) => {
      const data = periods.map(
        (p) => p.loadParameters?.[paramIndex]?.totalLoad ?? 0,
      );
      const seriesName =
        param.name || `${gettext("Parameter")} ${paramIndex + 1}`;
      const color = getChartSeriesColor(paramIndex);

      const baseSeries = {
        name: seriesName,
        data,
        itemStyle: { color },
        emphasis: {
          focus: "series",
        },
      };

      if (showResultsIn === "bar_graph") {
        return {
          ...baseSeries,
          type: "bar",
          barMaxWidth: 50,
        };
      }

      return {
        ...baseSeries,
        type: "line",
        smooth: false,
        symbol: "circle",
        symbolSize: 8,
        lineStyle: {
          width: 2,
        },
      };
    });

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: showResultsIn === "bar_graph" ? "shadow" : "line",
        },
        formatter: (params: any[]) => {
          if (!params?.length) {
            return "";
          }

          const periodIndex = params[0].dataIndex;
          const period = periods[periodIndex];

          let html = `<div style="margin-bottom: 8px; font-weight: bold;">${period.label}</div>`;

          params.forEach((param) => {
            const paramIndex = loadParameters.findIndex(
              (p) =>
                (p.name ||
                  `${gettext("Parameter")} ${loadParameters.indexOf(p) + 1}`) ===
                param.seriesName,
            );
            const pData = period.loadParameters?.[paramIndex];
            const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>`;
            const value = formatNumberLocale(param.value, 2);
            const avgConc = pData?.averageConcentration?.toFixed(2) ?? "-";
            const samples = pData?.sampleCount ?? 0;
            const interpolated = pData?.interpolated ? " *" : "";

            html += `<div style="margin: 4px 0;">${marker} ${param.seriesName}: <b>${value} kg${interpolated}</b></div>`;
            html += `<div style="margin-left: 18px; font-size: 11px; color: #666;">avg: ${avgConc} mg/l, samples: ${samples}</div>`;
          });

          html += `<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #ddd;">flow: ${period.cumulativeFlow?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>`;

          return html;
        },
      },
      legend: {
        data: series.map((s) => s.name),
        bottom: 0,
        itemGap: 20,
        textStyle: {
          fontSize: 12,
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "15%",
        top: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xAxisData,
        axisLabel: {
          rotate: periods.length > 6 ? 45 : 0,
          interval: 0,
        },
        axisTick: {
          alignWithLabel: true,
        },
      },
      yAxis: {
        type: "value",
        name: "kg",
        nameLocation: "end",
        nameGap: 10,
        axisLabel: {
          formatter: (value: number) => formatNumberLocale(value, 0),
        },
        splitLine: {
          lineStyle: {
            type: "dashed",
          },
        },
      },
      series,
    };
  }, [periods, loadParameters, showResultsIn]);

  if (!chartOptions) {
    return <div className="text-muted">{gettext("No data available")}</div>;
  }

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={chartOptions}
      style={{ height, width: "100%" }}
      opts={{ locale: "FI" }}
    />
  );
};

export default LoadCalculationChart;
